import { Save } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import {
  Box,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import CustomButton, {
  CustomSelect,
  CustomTextField,
} from '../../../Common/Components/Controls/InputControls';
import userHelper from '../../../Helpers/UserHelper';
import {
  CardWrapper,
  FooterContainer,
  FooterWrapper,
  PaperWrapper,
} from '../Common/CommonComponents';
import { accessControlService } from './AccessControlService';
import GroupPermissionsDragable from './Components/GroupPermissionsDragable';
import UserGroupDragable from './Components/UserGroupDragable';
import {
  PortalGroup,
  PortalGroupPermission,
  PortalPermission,
  PortalUser,
} from './Model/AccessControlModel';
import { Loader } from '../../../Common/Components/Loader';
import { showToast } from '../../../lib/toast';
import { useTranslation } from 'react-i18next';

interface ListComponentProps {
  items: Array<PortalUser | PortalGroup>;
  title: string;
  icon: React.ReactElement;
  setCurrentUser: React.Dispatch<React.SetStateAction<string>>;
  setCurrentGroup: React.Dispatch<React.SetStateAction<string>>;
  addIcon: React.ReactElement;
  addClickHandler: (
    event: React.MouseEvent<HTMLElement>,
    setAddPortalUser: React.Dispatch<React.SetStateAction<boolean>>,
    setEditPortalUser: React.Dispatch<React.SetStateAction<boolean>>,
    setAddPortalGroup: React.Dispatch<React.SetStateAction<boolean>>,
    setEditPortalGroup: React.Dispatch<React.SetStateAction<boolean>>,
  ) => void;
  setAddPortalUser: React.Dispatch<React.SetStateAction<boolean>>;
  setEditPortalUser: React.Dispatch<React.SetStateAction<boolean>>;
  setAddPortalGroup: React.Dispatch<React.SetStateAction<boolean>>;
  setEditPortalGroup: React.Dispatch<React.SetStateAction<boolean>>;
  resetAddstate: () => void;
  setGroupType: React.Dispatch<React.SetStateAction<string>>;
}

const isPortalUser = (item: PortalUser | PortalGroup): item is PortalUser => {
  return (item as PortalUser).Username !== undefined;
};

const handleClick = (
  event: React.MouseEvent<HTMLElement>,
  item: PortalUser | PortalGroup,
  resetAddstate: () => void,
  setCurrentUser: React.Dispatch<React.SetStateAction<string>>,
  setCurrentGroup: React.Dispatch<React.SetStateAction<string>>,
  setEditPortalUser: React.Dispatch<React.SetStateAction<boolean>>,
  setEditPortalGroup: React.Dispatch<React.SetStateAction<boolean>>,
  setGroupType: React.Dispatch<React.SetStateAction<string>>,
) => {
  const confirm = window.confirm(
    'Are you sure you want to leave the current operations?, if so your changes will be lost',
  );
  if (confirm) {
    resetAddstate();
    if (isPortalUser(item)) {
      setEditPortalGroup(false);
      setCurrentGroup('');
      setCurrentUser(item.Username);
      setEditPortalUser(true);
    } else {
      setEditPortalUser(false);
      setCurrentUser('');
      setCurrentGroup(item.GroupName);
      setGroupType(item.GroupType);
      setEditPortalGroup(true);
    }
  }
};

const addUserClickHandler = (
  event: React.MouseEvent<HTMLElement>,
  setAddPortalUser: React.Dispatch<React.SetStateAction<boolean>>,
  setEditPortalUser: React.Dispatch<React.SetStateAction<boolean>>,
  setAddPortalGroup: React.Dispatch<React.SetStateAction<boolean>>,
  setEditPortalGroup: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  setAddPortalUser(true);
  setEditPortalUser(false);
  setAddPortalGroup(false);
  setEditPortalGroup(false);
};
const addGroupClickHandler = (
  event: React.MouseEvent<HTMLElement>,
  setAddPortalUser: React.Dispatch<React.SetStateAction<boolean>>,
  setEditPortalUser: React.Dispatch<React.SetStateAction<boolean>>,
  setAddPortalGroup: React.Dispatch<React.SetStateAction<boolean>>,
  setEditPortalGroup: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  setAddPortalUser(false);
  setEditPortalUser(false);
  setAddPortalGroup(true);
  setEditPortalGroup(false);
};

const ListComponent: React.FC<ListComponentProps> = ({
  items,
  title,
  icon,
  setCurrentUser,
  setCurrentGroup,
  addIcon,
  addClickHandler,
  setAddPortalUser,
  setEditPortalUser,
  setAddPortalGroup,
  setEditPortalGroup,
  resetAddstate,
  setGroupType,
}) => (
  <>
    <Grid
      container
      alignItems="center"
      justifyContent="space-between"
      sx={{
        flexDirection: { xs: 'column', sm: 'row' },
        textAlign: { xs: 'center', sm: 'left' },
      }}
    >
      <Typography variant="h6">{title}</Typography>
      <CustomButton
        startIcon={addIcon}
        onClick={(e) =>
          addClickHandler(
            e,
            setAddPortalUser,
            setEditPortalUser,
            setAddPortalGroup,
            setEditPortalGroup,
          )
        }
        label="Add"
      />
    </Grid>

    <Divider sx={{ my: 2 }} />

    <Box
      sx={{
        overflowY: 'auto',
        maxHeight: { xs: 300, sm: 400, md: 600 },
        paddingX: { xs: 1, sm: 2 },
      }}
    >
      <List>
        {items.map((item) => (
          <ListItem
            key={isPortalUser(item) ? item.Username : item.GroupName}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'center', sm: 'flex-start' },
            }}
          >
            <ListItemButton
              sx={{
                width: '100%',
                justifyContent: { xs: 'center', sm: 'flex-start' },
              }}
              onClick={(e) =>
                handleClick(
                  e,
                  item,
                  resetAddstate,
                  setCurrentUser,
                  setCurrentGroup,
                  setEditPortalUser,
                  setEditPortalGroup,
                  setGroupType,
                )
              }
            >
              <ListItemIcon>{icon}</ListItemIcon>
              <ListItemText
                primary={isPortalUser(item) ? item.Username : item.GroupName}
                secondary={
                  isPortalUser(item)
                    ? userHelper.getNameFromId(item.Username)
                    : item.GroupType
                }
                sx={{ textAlign: { xs: 'center', sm: 'left' } }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  </>
);

const AccessControlHome: React.FC = () => {
  const { t, ready } = useTranslation('accesscontrol');
  const [portalUsers, setPortalUsers] = useState<PortalUser[]>([]);
  const [portalGroups, setPortalGroups] = useState<PortalGroup[]>([]);
  const [currentUser, setCurrentUser] = useState('');
  const [addPortalUser, setAddPortalUser] = useState(true);
  const [EditPortalUser, setEditPortalUser] = useState(false);
  const [addPortalGroup, setAddPortalGroup] = useState(false);
  const [EditPortalGroup, setEditPortalGroup] = useState(false);
  const [currentGroup, setCurrentGroup] = useState('');
  const [NewUserName, setNewUserName] = useState('');
  const [NewGroupName, setNewGroupName] = useState('');
  const [selectedGroups, setselectedUserGroups] = useState<string[]>([]);
  const [selectedPermissions, setselectedGroupPermissions] = useState<
    PortalPermission[]
  >([]);
  const [groupType, setGroupType] = useState('Internal');
  const [loading, setloading] = useState(false);

  useEffect(() => {
    loadPortalUsers().catch((err) => console.log(err));
    loadGroups().catch((err) => console.log(err));
  }, []);

  const loadPortalUsers = async () => {
    const portaluses = await accessControlService.getPortalUsers();
    setPortalUsers(portaluses.Items);
  };

  const loadGroups = async () => {
    const portalgroups = await accessControlService.getPortalGroups();
    setPortalGroups(portalgroups.Items);
  };

  const resetAddstate = () => {
    setAddPortalUser(false);
    setAddPortalGroup(false);
  };

  const reInitialize = () => {
    setPortalUsers([]);
    setPortalGroups([]);
    loadPortalUsers().catch((err) => console.log(err));
    loadGroups().catch((err) => console.log(err));
    setCurrentUser('');
    setNewUserName('');
    setAddPortalUser(true);
    setAddPortalGroup(false);
    setEditPortalUser(false);
    setEditPortalGroup(false);
    setEditPortalGroup(false);
    setloading(false);
  };

  const handleSaveUser = async () => {
    try {
      setloading(true);
      if (addPortalUser) {
        if (NewUserName === '') throw t('accesscontrol.error.user_name_empty');

        await accessControlService.SaveUser(NewUserName, selectedGroups);
        showToast({
          title: t('accesscontrol.success.user_saved', {
            userName: NewUserName,
          }),
          type: 'success',
        });
      } else if (currentUser) {
        await accessControlService.SaveUser(currentUser, selectedGroups);
        showToast({
          title: t('accesscontrol.success.user_saved', {
            userName: currentUser,
          }),
          type: 'success',
        });
      }
      reInitialize();
    } catch (err) {
      console.log(err);
      showToast({
        title: t('accesscontrol.error.user_save_failed', {
          userName: NewUserName,
        }),
        type: 'error',
      });
    } finally {
      setloading(false);
    }
  };

  const handleSaveGroup = async () => {
    try {
      setloading(true);
      if (addPortalGroup) {
        if (NewGroupName === '')
          throw t('accesscontrol.error.group_name_empty');
        const group: PortalGroup = {
          GroupName: NewGroupName,
          GroupType: groupType,
        };
        const portalgrouppermissions: PortalGroupPermission[] =
          selectedPermissions.map((permission) => ({
            PermissionName: permission.PermissionName,
            GroupName: NewGroupName,
          }));

        await accessControlService.SaveGroup(NewGroupName, group);
        await accessControlService.setGroupPermissions(
          NewGroupName,
          portalgrouppermissions,
        );
        showToast({
          title: t('accesscontrol.success.group_saved', {
            groupName: NewGroupName,
          }),
          type: 'success',
        });
      } else if (currentGroup) {
        const group: PortalGroup = {
          GroupName: currentGroup,
          GroupType: groupType,
        };
        const portalgrouppermissions: PortalGroupPermission[] =
          selectedPermissions.map((permission) => ({
            PermissionName: permission.PermissionName,
            GroupName: currentGroup,
          }));

        await accessControlService.SaveGroup(currentGroup, group);
        await accessControlService.setGroupPermissions(
          currentGroup,
          portalgrouppermissions,
        );
        showToast({
          title: t('accesscontrol.success.group_saved', {
            groupName: currentGroup,
          }),
          type: 'success',
        });
      }
      reInitialize();
    } catch (err) {
      showToast({
        title: t('accesscontrol.error.group_save_failed', {
          groupName: NewGroupName,
        }),
        type: 'error',
      });
      console.log(err);
      setloading(false);
    }
  };

  const handleDeleteUser = async () => {
    const confirm = window.confirm(
      t('accesscontrol.info.user_delete_confirm', { userName: currentUser }),
    );
    if (confirm) {
      try {
        setloading(true);
        accessControlService.DeleteUser(currentUser);
        showToast({
          title: t('accesscontrol.success.user_deleted', {
            userName: currentUser,
          }),
          type: 'success',
        });
      } catch (err) {
        showToast({
          title: t('accesscontrol.error.user_delete_failed', {
            userName: currentUser,
          }),
          type: 'error',
        });
        console.log(err);
      } finally {
        setloading(false);
        reInitialize();
      }
    }
  };

  const handleDeleteGroup = async () => {
    const confirm = window.confirm(
      'Are you sure you want to delete Group : ' + currentGroup,
    );
    if (confirm) {
      try {
        const group: PortalGroup = {
          GroupName: currentGroup,
          GroupType: groupType,
        };
        setloading(true);
        accessControlService.DeleteGroup(currentGroup, group);
        showToast({
          title: t('accesscontrol.success.group_deleted', {
            groupName: currentGroup,
          }),
          type: 'success',
        });
      } catch (err) {
        showToast({
          title: t('accesscontrol.error.group_delete_failed', {
            groupName: currentGroup,
          }),
          type: 'error',
        });
        console.log(err);
      } finally {
        setloading(false);
        reInitialize();
      }
    }
  };

  if (!ready) return <Loader loading={true} />;

  return (
    <div>
      <PaperWrapper>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t('accesscontrol.title')}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {loading ? (
          <Loader loading={loading} />
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <CardWrapper>
                <ListComponent
                  items={portalGroups}
                  title="Groups"
                  icon={<GroupIcon />}
                  addIcon={<GroupAddIcon />}
                  setCurrentUser={setCurrentUser}
                  setCurrentGroup={setCurrentGroup}
                  addClickHandler={addGroupClickHandler}
                  setAddPortalUser={setAddPortalUser}
                  setEditPortalUser={setEditPortalUser}
                  setAddPortalGroup={setAddPortalGroup}
                  setEditPortalGroup={setEditPortalGroup}
                  resetAddstate={resetAddstate}
                  setGroupType={setGroupType}
                />
              </CardWrapper>
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={6}>
              <CardWrapper>
                <ListComponent
                  items={portalUsers}
                  title="Portal Users"
                  icon={<PersonIcon color="secondary" />}
                  setCurrentUser={setCurrentUser}
                  setCurrentGroup={setCurrentGroup}
                  addIcon={<PersonAddAlt1Icon />}
                  addClickHandler={addUserClickHandler}
                  setAddPortalUser={setAddPortalUser}
                  setEditPortalUser={setEditPortalUser}
                  setAddPortalGroup={setAddPortalGroup}
                  setEditPortalGroup={setEditPortalGroup}
                  resetAddstate={resetAddstate}
                  setGroupType={setGroupType}
                />
              </CardWrapper>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <CardWrapper sx={{ mt: 3, width: '100%' }}>
                {addPortalUser &&
                  CreatePortalUser(
                    NewUserName,
                    setNewUserName,
                    setselectedUserGroups,
                  )}
                {EditPortalUser &&
                  currentUser !== '' &&
                  UpdatePortalUser(
                    currentUser,
                    handleDeleteUser,
                    setselectedUserGroups,
                  )}
                {addPortalGroup &&
                  CreatePortalGroup(
                    NewGroupName,
                    setNewGroupName,
                    setselectedGroupPermissions,
                    setGroupType,
                    groupType,
                  )}
                {EditPortalGroup &&
                  currentGroup !== '' &&
                  UpdatePortalGroup(
                    currentGroup,
                    handleDeleteGroup,
                    setselectedGroupPermissions,
                    setGroupType,
                    groupType,
                  )}
              </CardWrapper>
              <FooterWrapper>
                <FooterContainer>
                  <CustomButton
                    startIcon={<Save />}
                    label={
                      addPortalUser || EditPortalUser
                        ? 'Save User'
                        : 'Save Group'
                    }
                    onClick={
                      addPortalUser || EditPortalUser
                        ? handleSaveUser
                        : handleSaveGroup
                    }
                  />
                </FooterContainer>
              </FooterWrapper>
            </Grid>
          </Grid>
        )}
      </PaperWrapper>
    </div>
  );
};

export default AccessControlHome;

function UpdatePortalUser(
  currentUser: string,
  handleDeleteUser: () => Promise<void>,
  setselectedUserGroups: React.Dispatch<React.SetStateAction<string[]>>,
) {
  return (
    <>
      <Box sx={{ width: '100%' }}>
        <Grid
          container
          spacing={2}
          alignItems="center"
          sx={{
            flexWrap: 'wrap',
            textAlign: { xs: 'center', sm: 'left' }, // Center on mobile, left-align on larger screens
          }}
        >
          <Grid item xs={12} sm="auto">
            <Typography>
              <PersonIcon />
            </Typography>
          </Grid>

          <Grid item xs={12} sm>
            <Typography
              variant="h6"
              sx={{ width: '100%', textAlign: { xs: 'center', sm: 'left' } }} // Centered text on mobile
            >
              {currentUser}
            </Typography>
          </Grid>

          <Grid item xs={12} sm="auto">
            <CustomButton
              onClick={handleDeleteUser}
              startIcon={<DeleteIcon />}
              label="Delete"
              sx={{ width: { xs: '100%', sm: 'auto' } }} // Full width on mobile for better touch experience
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mt: 2, mb: 2 }} />

      <UserGroupDragable
        username={currentUser}
        setselectedUserGroups={setselectedUserGroups}
      />
    </>
  );
}

function CreatePortalUser(
  NewUserName: string,
  setNewUserName: React.Dispatch<React.SetStateAction<string>>,
  setselectedUserGroups: React.Dispatch<React.SetStateAction<string[]>>,
) {
  return (
    <>
      <Box sx={{ width: '100%' }}>
        <Grid
          container
          spacing={2}
          alignItems="center"
          sx={{
            flexWrap: 'wrap',
            textAlign: { xs: 'center', sm: 'left' }, // Center text on mobile, left-align on larger screens
          }}
        >
          <Grid item xs={12} sm="auto">
            <Typography>
              <PersonIcon />
            </Typography>
          </Grid>

          <Grid item xs={12} sm>
            <CustomTextField
              placeholder="User Name"
              name="Username"
              value={NewUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              sx={{ width: '100%' }} // Full width on mobile for better usability
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mt: 2, mb: 2 }} />
      <UserGroupDragable
        username=""
        setselectedUserGroups={setselectedUserGroups}
      />
    </>
  );
}

function UpdatePortalGroup(
  currentGroup: string,
  handleDeleteGroup: () => Promise<void>,
  setselectedGroupPermissions: React.Dispatch<
    React.SetStateAction<PortalPermission[]>
  >,
  setGroupType: React.Dispatch<React.SetStateAction<string>>,
  groupType: string,
) {
  return (
    <>
      <Box sx={{ width: '100%' }}>
        <Grid
          container
          spacing={2}
          alignItems="center"
          sx={{
            width: '100%',
            flexWrap: 'wrap',
            textAlign: { xs: 'center', sm: 'left' },
          }}
        >
          <Grid item xs={12} sm="auto">
            <Typography>
              <GroupIcon />
            </Typography>
          </Grid>

          <Grid item xs={12} sm>
            <Typography variant="h6">{currentGroup}</Typography>
          </Grid>

          <Grid item xs={12} sm="auto">
            <CustomSelect
              name="GroupType"
              value={groupType}
              label="GroupType"
              onChange={(e) => setGroupType(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              {['Internal', 'Partner'].map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </CustomSelect>
          </Grid>

          <Grid item xs={12} sm="auto">
            <CustomButton
              onClick={handleDeleteGroup}
              startIcon={<DeleteIcon />}
              label="Delete"
            />
          </Grid>
        </Grid>
        <Divider sx={{ mt: 2, mb: 2 }} />
      </Box>
      <GroupPermissionsDragable
        groupname={currentGroup}
        setselectedGroupPermissions={setselectedGroupPermissions}
      />
    </>
  );
}

function CreatePortalGroup(
  NewGroupName: string,
  setNewGroupName: React.Dispatch<React.SetStateAction<string>>,
  setselectedGroupPermissions: React.Dispatch<
    React.SetStateAction<PortalPermission[]>
  >,
  setGroupType: React.Dispatch<React.SetStateAction<string>>,
  groupType: string,
) {
  return (
    <>
      <Box sx={{ width: '100%' }}>
        <Grid
          container
          spacing={2}
          alignItems="center"
          sx={{
            flexWrap: 'wrap',
            textAlign: { xs: 'center', sm: 'left' }, // Center on mobile, left on larger screens
          }}
        >
          <Grid item xs={12} sm="auto">
            <Typography>
              <GroupIcon />
            </Typography>
          </Grid>

          <Grid item xs={12} sm>
            <CustomTextField
              placeholder="Group Name"
              name="GroupName"
              value={NewGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              sx={{ width: '100%' }} // Full width on mobile
            />
          </Grid>

          <Grid item xs={12} sm="auto">
            <CustomSelect
              name="GroupType"
              value={groupType}
              label="GroupType"
              onChange={(e) => setGroupType(e.target.value)}
              sx={{ minWidth: 150 }} // Ensures proper width on larger screens
            >
              {['Internal', 'Partner'].map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </CustomSelect>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mt: 2, mb: 2 }} />
      <GroupPermissionsDragable
        groupname=""
        setselectedGroupPermissions={setselectedGroupPermissions}
      />
    </>
  );
}
